// Version: 2.2
// v2.2: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v2.1: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { authFetch } from './auth';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const svcColour = s => s === 'running' ? '#30d158' : s === 'stopped' ? '#e6b800' : '#ff2d55';
const svcLabel  = s => s === 'running' ? 'Running' : s === 'stopped' ? 'Stopped' : 'Not Installed';

function semverGt(a, b) {
  const parse = v => String(v || '0').split('.').map(x => parseInt(x, 10) || 0);
  const pa = parse(a), pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
    }) + ' GMT';
  } catch { return iso; }
}

function deviceOnline(dev) {
  if (!dev.last_seen) return false;
  try {
    return (Date.now() - new Date(dev.last_seen).getTime()) / 60000 < 15;
  } catch { return false; }
}

const cell = { padding:'8px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:160 };
const th   = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--text-muted)',
               textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap',
               background:'rgba(255,255,255,0.02)', borderBottom:'1px solid var(--border)' };


// ── Global Agent Update Admin ──────────────────────────────────────────────────

function AgentUpdateAdmin() {
  const isMobile = useIsMobile();
  const [staged, setStaged]         = useState(null);
  const [published, setPublished]   = useState(null);
  const [channel, setChannel]       = useState('auto');
  const [publishing, setPublishing] = useState(false);
  const [msg, setMsg]               = useState(null);
  const [expanded, setExpanded]     = useState(false);
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadFile, setUploadFile]       = useState(null);
  const [uploading, setUploading]         = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    authFetch('/api/admin/agent-update/staged').then(r => r.json()).then(d => setStaged(d.staged || null)).catch(() => {});
    authFetch('/api/admin/agent-update/published').then(r => r.json()).then(d => setPublished(d.published || null)).catch(() => {});
    authFetch('/api/admin/agent-update/global-channel').then(r => r.json()).then(d => setChannel(d.channel || 'auto')).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const flash = (text, err=false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 4000);
  };

  const handleUpload = async () => {
    if (!uploadVersion || !uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('version', uploadVersion);
      fd.append('file', uploadFile);
      const r = await authFetch('/api/admin/agent-update/stage', { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Upload failed');
      flash(`Agent v${uploadVersion} staged successfully`);
      setUploadVersion('');
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (e) { flash(e.message, true); }
    setUploading(false);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const r = await authFetch('/api/admin/agent-update/publish', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Publish failed');
      flash(`Agent v${d.published?.version} published successfully`);
      load();
    } catch (e) { flash(e.message, true); }
    setPublishing(false);
  };

  const handleChannelChange = async (newChannel) => {
    try {
      const r = await authFetch('/api/admin/agent-update/global-channel', {
        method: 'POST', body: JSON.stringify({ channel: newChannel }),
      });
      if (!r.ok) throw new Error('Failed');
      setChannel(newChannel);
      flash(`Global update channel set to ${newChannel}`);
    } catch { flash('Failed to update channel', true); }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London',
      }) + ' GMT';
    } catch { return iso; }
  };

  const hasNewStaged = staged && (!published || staged.version !== published.version) && !staged.multiple_msi;

  return (
    <div style={{borderRadius:8,border:'1px solid var(--border)',background:'rgba(255,255,255,0.02)',marginBottom:20,padding:16}}>
      <div style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer'}} onClick={() => setExpanded(e => !e)}>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14,display:'flex',alignItems:'center',gap:8}}>
            Agent Update Management
            {hasNewStaged && (
              <span style={{fontSize:11,padding:'2px 7px',borderRadius:4,background:'rgba(0,212,255,0.1)',border:'1px solid rgba(0,212,255,0.3)',color:'var(--accent)',fontWeight:600}}>
                Staged: v{staged.version}
              </span>
            )}
          </div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>
            Published: {published ? `v${published.version}` : 'none'}
            {published?.published_at && ` · ${formatDate(published.published_at)}`}
            {' · '}Channel: <span style={{color:'var(--accent)'}}>{channel}</span>
          </div>
        </div>
        <span style={{fontSize:12,color:'var(--text-muted)',flexShrink:0}}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:16}}>
          {msg && (
            <div style={{marginBottom:12,padding:'8px 12px',borderRadius:6,fontSize:12,
              background: msg.err ? 'rgba(255,45,85,0.08)' : 'rgba(48,209,88,0.08)',
              border: `1px solid ${msg.err ? 'rgba(255,45,85,0.3)' : 'rgba(48,209,88,0.3)'}`,
              color: msg.err ? '#ff2d55' : '#30d158'}}>
              {msg.text}
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:16,marginBottom:16}}>
            {/* Staged */}
            <div style={{padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'rgba(255,255,255,0.02)'}}>
              <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>Staged</div>
              {staged ? (
                <>
                  <div style={{fontSize:18,fontWeight:700,color:'var(--accent)'}}>v{staged.version}</div>
                  {staged.detected_msi && (
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>{staged.detected_msi}</div>
                  )}
                  {staged.multiple_msi && (
                    <div style={{fontSize:11,color:'#ff2d55',marginTop:4,fontWeight:600}}>⚠ Multiple MSI files detected — remove all but one before publishing</div>
                  )}
                  {staged.staged_at && (
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Staged {formatDate(staged.staged_at)}</div>
                  )}
                </>
              ) : (
                <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:8}}>No staged version.</div>
              )}

              {/* Upload new MSI */}
              <div style={{marginTop:12,borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>Upload New Version</div>
                <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                  <input className="input-sm"
                    type="text"
                    placeholder="Version e.g. 4.1.1"
                    value={uploadVersion}
                    onChange={e => setUploadVersion(e.target.value)}
                    style={{ width:130 }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".msi"
                    onChange={e => setUploadFile(e.target.files[0] || null)}
                    style={{fontSize:11,color:'var(--text-muted)',flex:1,minWidth:0}}
                  />
                  <button className="btn btn-secondary btn-sm" style={{ opacity: !uploadVersion||!uploadFile||uploading ? 0.5:1, flexShrink: 0 }}
                    onClick={handleUpload}
                    disabled={!uploadVersion || !uploadFile || uploading}
                   >
                    {uploading ? 'Uploading...' : '↑ Stage'}
                  </button>
                </div>
              </div>
            </div>

            {/* Published */}
            <div style={{padding:'12px 14px',borderRadius:8,border:'1px solid var(--border)',background:'rgba(255,255,255,0.02)'}}>
              <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>Published</div>
              {published ? (
                <>
                  <div style={{fontSize:18,fontWeight:700,color:'#30d158'}}>v{published.version}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>{published.msi_filename}</div>
                  {published.published_at && <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Published {formatDate(published.published_at)}</div>}
                  {published.sha256 && <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2,fontFamily:'monospace'}}>{published.sha256.substring(0,16)}...</div>}
                </>
              ) : (
                <div style={{fontSize:12,color:'var(--text-muted)'}}>Nothing published yet.</div>
              )}
            </div>
          </div>

          {/* Publish button */}
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
            <button className="btn btn-primary" style={{ opacity: !hasNewStaged || publishing ? 0.5 : 1 }}
              onClick={handlePublish}
              disabled={!hasNewStaged || publishing}
             
             >
              {publishing ? <span className="spinner"/> : `↑ Publish v${staged?.version || '?'} to all agents`}
            </button>
            {!hasNewStaged && staged && (
              <span style={{fontSize:11,color:'var(--text-muted)'}}>Staged version matches published — no action needed.</span>
            )}
            {!staged && (
              <span style={{fontSize:11,color:'var(--text-muted)'}}>Stage a new version to enable publishing.</span>
            )}
          </div>

          {/* Global channel */}
          <div style={{borderTop:'1px solid var(--border)',paddingTop:14}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Global Update Channel</div>
            <div style={{display:'flex',gap:8}}>
              {['auto','manual'].map(c => (
                <button className="btn btn-secondary btn-sm" aria-pressed={channel===c} key={c} onClick={() => handleChannelChange(c)}
                 >
                  {c === 'auto' ? 'Auto (agents self-update)' : 'Manual (admin pushes only)'}
                </button>
              ))}
            </div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>
              Per-client overrides are available in each client's Device Hub agent settings.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ClientManagement ──────────────────────────────────────────────────────

// ── ProductivityHub inline (data view with Config Drift sub-tab) ──────────────

function ProductivityHubInline({ slug }) {
  const [subTab, setSubTab] = useState('m365');
  const SUB_TABS = [
    { id: 'm365',  label: 'Microsoft 365' },
    { id: 'drift', label: 'Config Drift' },
  ];
  return (
    <div>
      <SubTabBar tabs={SUB_TABS} active={subTab} onChange={setSubTab} />
      {subTab === 'm365'  && <ProductivityHub slug={slug} />}
      {subTab === 'drift' && <TrustM365DriftDetail slug={slug} />}
    </div>
  );
}

// ── TrustM365 Config Drift detail view ────────────────────────────────────────

function TrustM365DriftDetail({ slug }) {
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`/api/m365/drift/${slug}`)
      .then(r => r.json())
      .then(d => { setDrift(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{padding:'12px 0'}}><span className="spinner"/></div>;
  if (!drift || !drift.available) return (
    <div style={{padding:'16px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text-muted)'}}>
      No TrustM365 data available. Add a TrustM365 Tenant UUID in Client Settings to enable M365 config drift monitoring.
    </div>
  );

  const status = String(drift.overall_status || '');
  const statusColor = status === 'drifted' ? '#ff2d55' : status === 'clean' ? '#30d158' : status === 'partial' ? '#e6b800' : 'var(--text-muted)';
  const statusLabel = status === 'drifted' ? 'Drift Detected' : status === 'clean' ? 'All Clean' : status === 'partial' ? 'Partially Monitored' : status === 'unconfigured' ? 'No Baseline Set' : status || 'Unknown';
  const areas = Array.isArray(drift.areas) ? drift.areas : [];

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:700,color:statusColor,background:`${statusColor}18`,border:`1px solid ${statusColor}40`,borderRadius:4,padding:'3px 10px'}}>{statusLabel}</span>
        {Number(drift.drifted_areas||0) > 0 && <span style={{fontSize:13,color:'var(--text-muted)'}}>{Number(drift.total_drifts||0)} change{Number(drift.total_drifts||0)!==1?'s':''} across {Number(drift.drifted_areas||0)} area{Number(drift.drifted_areas||0)!==1?'s':''}</span>}
        {drift.polled_at && <span style={{fontSize:12,color:'var(--text-muted)',marginLeft:'auto'}}>Updated {fmtDate(String(drift.polled_at))}</span>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {areas.map((area,i) => {
          const hasDrift = area.drift?.status === 'drifted' && Number(area.drift?.drift_count||0) > 0;
          const isClean = area.drift?.status === 'clean' || (area.drift?.status === 'drifted' && Number(area.drift?.drift_count||0) === 0);
          const noBaseline = !area.hasBaseline;
          const dotColor = hasDrift ? '#ff2d55' : isClean ? '#30d158' : noBaseline ? 'var(--text-muted)' : '#e6b800';
          return (
            <div key={area.areaKey||i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8}}>
              <span style={{color:dotColor,fontSize:10,flexShrink:0}}>●</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{String(area.displayName||area.areaKey||'')}</div>
                {hasDrift && <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{Number(area.drift.drift_count)} change{Number(area.drift.drift_count)!==1?'s':''}</div>}
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                {noBaseline ? <span style={{fontSize:11,color:'var(--text-muted)'}}>No baseline</span>
                  : hasDrift ? <span style={{fontSize:11,fontWeight:600,color:'#ff2d55'}}>Drifted</span>
                  : isClean  ? <span style={{fontSize:11,color:'#30d158'}}>Clean</span>
                  : <span style={{fontSize:11,color:'#e6b800'}}>Pending</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── TrustM365 Drift Panel (admin overview tile) ───────────────────────────────

function TrustM365DriftPanel({ slug }) {
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`/api/m365/drift/${slug}`)
      .then(r => r.json())
      .then(d => { setDrift(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return null;
  if (!drift || !drift.available) return null;

  const status = String(drift.overall_status || '');
  const statusColor = status === 'drifted' ? '#ff2d55' : status === 'clean' ? '#30d158' : status === 'partial' ? '#e6b800' : 'var(--text-muted)';
  const statusLabel = status === 'drifted' ? 'Drift Detected' : status === 'clean' ? 'All Clean' : status === 'partial' ? 'Partially Monitored' : status === 'unconfigured' ? 'No Baseline Set' : status || 'Unknown';
  const driftedAreas = Number(drift.drifted_areas) || 0;
  const totalDrifts = Number(drift.total_drifts) || 0;
  const polledAt = drift.polled_at ? fmtDate(String(drift.polled_at)) : null;
  const areas = Array.isArray(drift.areas) ? drift.areas : [];

  return (
    <div style={{marginTop:16}}>
      <SectionTitle>M365 Config Drift</SectionTitle>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'12px 14px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:driftedAreas > 0 ? 10 : 0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:11,fontWeight:700,color:statusColor,background:`${statusColor}18`,border:`1px solid ${statusColor}40`,borderRadius:4,padding:'2px 8px'}}>{statusLabel}</span>
            {driftedAreas > 0 && <span style={{fontSize:12,color:'var(--text-muted)'}}>{totalDrifts} change{totalDrifts!==1?'s':''} across {driftedAreas} area{driftedAreas!==1?'s':''}</span>}
          </div>
          {polledAt && <span style={{fontSize:11,color:'var(--text-muted)'}}>Updated {polledAt}</span>}
        </div>
        {driftedAreas > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {areas.filter(a => a.drift?.status === 'drifted' && Number(a.drift?.drift_count||0) > 0).slice(0,5).map((a,i) => (
              <div key={a.areaKey||i} style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                <span style={{color:'#ff2d55',fontSize:10}}>●</span>
                <span style={{color:'var(--text)'}}>{String(a.displayName||a.areaKey||'')}</span>
                <span style={{color:'var(--text-muted)',marginLeft:'auto'}}>{Number(a.drift.drift_count)} change{Number(a.drift.drift_count)!==1?'s':''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AgentNodes() {
  const isMobile = useIsMobile();
  const [devices, setDevices]     = useState([]);
  const [clients, setClients]     = useState([]);
  const [published, setPublished] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filterClient, setFilterClient]   = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterVersion, setFilterVersion] = useState('');
  const [search, setSearch]               = useState('');

  const load = useCallback((silent) => {
    if (!silent) setLoading(true);
    Promise.all([
      authFetch('/api/admin/agent-nodes').then(r => r.json()),
      authFetch('/api/admin/agent-update/published').then(r => r.json()),
    ]).then(([nodes, pub]) => {
      setDevices(nodes.devices || []);
      setClients(nodes.clients || []);
      setPublished(pub.published || null);
    }).catch(() => {}).finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds — silent, so the table doesn't flash to
  // a loading state on each background tick.
  useEffect(() => {
    const interval = setInterval(() => load(true), 30000);
    return () => clearInterval(interval);
  }, [load]);

  const publishedVersion = published?.version || null;

  const filtered = devices.filter(d => {
    const online = deviceOnline(d);
    if (filterClient  && d.client_slug !== filterClient) return false;
    if (filterStatus  && (online ? 'online' : 'offline') !== filterStatus) return false;
    if (filterVersion && d.agent_version !== filterVersion) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(d.display_name||'').toLowerCase().includes(q) &&
          !(d.hostname||'').toLowerCase().includes(q) &&
          !(d.client_slug||'').toLowerCase().includes(q) &&
          !(d.logged_in_user||'').toLowerCase().includes(q) &&
          !(d.hardware?.model||'').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const versions     = [...new Set(devices.map(d => d.agent_version).filter(Boolean))].sort();
  const onlineCount  = devices.filter(deviceOnline).length;
  const offlineCount = devices.length - onlineCount;
  const outdatedCount = publishedVersion ? devices.filter(d => d.agent_version && semverGt(publishedVersion, d.agent_version)).length : 0;
  const rebootCount  = devices.filter(d => d.pending_reboot?.pending).length;

  const inputStyle = { width:'100%' };

  return (
    <div style={{maxWidth:1400, margin:'0 auto', padding:'24px 16px'}}>
      {/* Header */}
      <div style={{marginBottom:20}}>
        <h1 style={{fontSize:22,fontWeight:700,margin:0}}>Agent Nodes</h1>
        <div style={{fontSize:13,color:'var(--text-muted)',marginTop:4}}>All enrolled Clearview agents across the estate</div>
      </div>

      {/* Agent Update Management — staging, publishing, global update channel */}
      <AgentUpdateAdmin />

      {/* Summary tiles */}
      <div className="stat-tiles">
        {[
          ['Total',         devices.length,  'accent'],
          ['Online',        onlineCount,      'green'],
          ['Offline',       offlineCount,     offlineCount > 0 ? 'red' : 'muted'],
          ['Outdated',      outdatedCount,    outdatedCount > 0 ? 'amber' : 'green'],
          ['Reboot needed', rebootCount,      rebootCount > 0 ? 'amber' : 'green'],
        ].map(([label, value, tone]) => (
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'grid',gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr',gap:8,marginBottom:8}}>
        <div>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Search</div>
          <input className="input-sm" type="text" placeholder="Hostname, user, hardware model..."
            value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Client</div>
          <select className="input-sm" value={filterClient} onChange={e => setFilterClient(e.target.value)} style={inputStyle}>
            <option value="">All clients</option>
            {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name || c.slug}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Status</div>
          <select className="input-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle}>
            <option value="">All statuses</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Agent Version</div>
          <select className="input-sm" value={filterVersion} onChange={e => setFilterVersion(e.target.value)} style={inputStyle}>
            <option value="">All versions</option>
            {versions.map(v => <option key={v} value={v}>v{v}</option>)}
          </select>
        </div>
      </div>

      <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:10,minHeight:24}}>
        {(filterClient || filterStatus || filterVersion || search) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilterClient(''); setFilterStatus(''); setFilterVersion(''); setSearch(''); }}
           >
            ✕ Clear filters
          </button>
        )}
        <span style={{fontSize:12,color:'var(--text-muted)'}}>
          {filtered.length === devices.length
            ? `${devices.length} device${devices.length !== 1 ? 's' : ''}`
            : `${filtered.length} of ${devices.length} devices`}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text-muted)',fontSize:13}}>Loading devices...</div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text-muted)',fontSize:13}}>No devices found</div>
      ) : isMobile ? (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {filtered.map(dev => {
            const online   = deviceOnline(dev);
            const outdated = publishedVersion && dev.agent_version && semverGt(publishedVersion, dev.agent_version);
            const svcs     = dev.service_statuses || [];
            const badSvcs  = svcs.filter(s => s.status !== 'running');
            return (
              <div key={`${dev.client_slug}-${dev.device_id}`} style={{
                borderRadius:8, border:'1px solid var(--border)', background:'var(--card-bg,rgba(255,255,255,0.02))',
                padding:'12px 14px',
              }}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                    <span style={{width:8,height:8,borderRadius:'50%',flexShrink:0,
                      background: online ? '#30d158' : '#ff2d55'}}/>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {dev.display_name || dev.hostname || dev.device_id?.substring(0,8) || '—'}
                      </div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>
                        {clients.find(c => c.slug === dev.client_slug)?.display_name || dev.client_slug}
                      </div>
                    </div>
                  </div>
                  {dev.pending_reboot?.pending && (
                    <span style={{flexShrink:0,fontSize:10,padding:'1px 5px',borderRadius:3,
                      background:'rgba(230,184,0,0.15)',border:'1px solid rgba(230,184,0,0.3)',
                      color:'#e6b800',fontWeight:600,whiteSpace:'nowrap'}}>Reboot</span>
                  )}
                </div>
                <div style={{display:'flex',gap:14,flexWrap:'wrap',fontSize:11,color:'var(--text-muted)',marginBottom:svcs.length?8:0}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>OS</div>
                    <div>{dev.os_friendly || dev.os || '—'}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>Agent</div>
                    <div style={{color: outdated ? '#e6b800' : '#30d158', fontWeight:600}}>
                      {dev.agent_version ? `v${dev.agent_version}` : '—'}{outdated && ' ↑'}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>User</div>
                    <div>{dev.logged_in_user || '—'}</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>Last Seen</div>
                    <div>{formatDate(dev.last_seen)}</div>
                  </div>
                </div>
                {dev.hardware && (
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:svcs.length?8:0}}>
                    {`${dev.hardware.manufacturer||''} ${dev.hardware.model||''}`.trim()}
                  </div>
                )}
                {svcs.length > 0 && (
                  badSvcs.length === 0 ? (
                    <span style={{fontSize:11,color:'#30d158',fontWeight:600}}>All services OK</span>
                  ) : (
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {badSvcs.map(s => (
                        <span key={s.name} style={{fontSize:10,padding:'2px 6px',borderRadius:3,
                          background:`${svcColour(s.status)}18`,
                          border:`1px solid ${svcColour(s.status)}40`,
                          color:svcColour(s.status),fontWeight:600,whiteSpace:'nowrap'}}>
                          {s.display||s.name}: {svcLabel(s.status)}
                        </span>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })}
          <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'center',padding:'4px 0'}}>
            {filtered.length} device{filtered.length !== 1 ? 's' : ''}
            {filtered.length !== devices.length && ` (filtered from ${devices.length})`}
          </div>
        </div>
      ) : (
        <div style={{borderRadius:8,border:'1px solid var(--border)',overflow:'hidden'}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,tableLayout:'fixed',minWidth:900}}>
              <colgroup>
                <col style={{width:28}}/>   {/* status */}
                <col style={{width:'16%'}}/> {/* hostname */}
                <col style={{width:'13%'}}/> {/* client */}
                <col style={{width:'13%'}}/> {/* OS */}
                <col style={{width:72}}/> {/* agent */}
                <col style={{width:'18%'}}/> {/* hardware */}
                <col style={{width:'13%'}}/> {/* last seen */}
                <col style={{width:'11%'}}/> {/* user */}
                <col style={{width:'12%'}}/> {/* services */}
              </colgroup>
              <thead>
                <tr>
                  <th style={th}/>
                  {['Hostname','Client','OS','Agent','Hardware','Last Seen','User','Services'].map(h => (
                    <th key={h} style={{...th,textAlign:'left'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((dev, i) => {
                  const online   = deviceOnline(dev);
                  const outdated = publishedVersion && dev.agent_version && semverGt(publishedVersion, dev.agent_version);
                  const svcs     = dev.service_statuses || [];
                  const badSvcs  = svcs.filter(s => s.status !== 'running');
                  return (
                    <tr key={`${dev.client_slug}-${dev.device_id}`}
                      style={{borderBottom:'1px solid var(--border)',
                        background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}}>

                      <td style={{...cell,width:28,paddingRight:0}}>
                        <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',
                          background: online ? '#30d158' : '#ff2d55',flexShrink:0}}/>
                      </td>

                      <td style={{...cell,fontWeight:600,color:'var(--text)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,overflow:'hidden'}}>
                          <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {dev.display_name || dev.hostname || dev.device_id?.substring(0,8) || '—'}
                          </span>
                          {dev.pending_reboot?.pending && (
                            <span style={{flexShrink:0,fontSize:10,padding:'1px 4px',borderRadius:3,
                              background:'rgba(230,184,0,0.15)',border:'1px solid rgba(230,184,0,0.3)',
                              color:'#e6b800',fontWeight:600}}>Reboot</span>
                          )}
                        </div>
                      </td>

                      <td style={{...cell,color:'var(--text-muted)'}}>
                        {clients.find(c => c.slug === dev.client_slug)?.display_name || dev.client_slug}
                      </td>

                      <td style={{...cell,color:'var(--text-muted)'}}>
                        {dev.os_friendly || dev.os || '—'}
                      </td>

                      <td style={{...cell}}>
                        <span style={{color: outdated ? '#e6b800' : '#30d158', fontWeight: outdated ? 600 : 500}}>
                          {dev.agent_version ? `v${dev.agent_version}` : '—'}
                          {outdated && <span style={{marginLeft:3,fontSize:10}}>↑</span>}
                        </span>
                      </td>

                      <td style={{...cell,color:'var(--text-muted)'}}>
                        {dev.hardware ? `${dev.hardware.manufacturer||''} ${dev.hardware.model||''}`.trim() : '—'}
                      </td>

                      <td style={{...cell,color:'var(--text-muted)',fontSize:11}}>
                        {formatDate(dev.last_seen)}
                      </td>

                      <td style={{...cell,color:'var(--text-muted)'}}>
                        {dev.logged_in_user || '—'}
                      </td>

                      <td style={{...cell}}>
                        {svcs.length === 0 ? (
                          <span style={{color:'var(--text-muted)'}}>—</span>
                        ) : badSvcs.length === 0 ? (
                          <span style={{color:'#30d158',fontWeight:600}}>All OK</span>
                        ) : (
                          <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
                            {badSvcs.map(s => (
                              <span key={s.name} title={`${s.display||s.name}: ${svcLabel(s.status)}`}
                                style={{fontSize:10,padding:'1px 4px',borderRadius:3,
                                  background:`${svcColour(s.status)}18`,
                                  border:`1px solid ${svcColour(s.status)}40`,
                                  color:svcColour(s.status),fontWeight:600,whiteSpace:'nowrap'}}>
                                {s.display||s.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{padding:'8px 12px',borderTop:'1px solid var(--border)',fontSize:11,color:'var(--text-muted)',
            background:'rgba(255,255,255,0.02)'}}>
            {filtered.length} device{filtered.length !== 1 ? 's' : ''}
            {filtered.length !== devices.length && ` (filtered from ${devices.length})`}
          </div>
        </div>
      )}
    </div>
  );
}